/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

// Product block size selector - Add to cart on click
(function() {
  // Create backdrop element for mobile modal
  let backdrop = null;
  let currentOpenModal = null;
  
  // Function to initialize backdrop
  function initBackdrop() {
    if (!backdrop) {
      backdrop = document.createElement('div');
      backdrop.className = 'product-block-sizes-backdrop';
      document.body.appendChild(backdrop);
      
      // Handle backdrop clicks
      backdrop.addEventListener('click', function() {
        closeSizeModal();
      });
    }
  }
  
  // Function to open modal (mobile)
  function openSizeModal(sizesContainer) {
    console.log('Opening size modal');
    initBackdrop();
    currentOpenModal = sizesContainer;
    sizesContainer.classList.add('is-modal-open');
    backdrop.classList.add('is-active');
    document.body.style.overflow = 'hidden';
  }
  
  // Function to close modal (mobile)
  function closeSizeModal() {
    console.log('Closing size modal');
    if (currentOpenModal) {
      currentOpenModal.classList.remove('is-modal-open');
    }
    if (backdrop) {
      backdrop.classList.remove('is-active');
    }
    document.body.style.overflow = '';
    currentOpenModal = null;
  }
  
  // Use event delegation for all size selector interactions
  // This works for both initial and dynamically loaded products
  document.addEventListener('click', function(e) {
    // Handle trigger button clicks (mobile)
    const trigger = e.target.closest('.product-block__size-trigger');
    if (trigger) {
      e.preventDefault();
      e.stopPropagation();
      
      const sizesContainer = trigger.nextElementSibling;
      if (sizesContainer && sizesContainer.classList.contains('product-block__sizes')) {
        openSizeModal(sizesContainer);
      }
      return;
    }
    
    // Handle close button clicks
    const closeBtn = e.target.closest('.product-block__sizes-close');
    if (closeBtn) {
      e.preventDefault();
      e.stopPropagation();
      closeSizeModal();
      return;
    }
    
    // Handle size button clicks
    const sizeButton = e.target.closest('.product-block__size');
    if (sizeButton && !sizeButton.disabled) {
      e.preventDefault();
      e.stopPropagation();
      
      console.log('Size button clicked:', sizeButton.textContent, 'Variant ID:', sizeButton.getAttribute('data-variant-id'));
      
      const variantId = sizeButton.getAttribute('data-variant-id');
      if (!variantId) return;
      
      // Add loading state
      const originalText = sizeButton.textContent;
      sizeButton.textContent = '...';
      sizeButton.disabled = true;
      
      // Add to cart
      console.log('Attempting to add variant to cart:', variantId);
      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1
        })
      })
      .then(response => {
        console.log('Cart add response:', response);
        return response.json();
      })
      .then(data => {
        console.log('Cart add success:', data);
        // Show success state
        sizeButton.textContent = '✓';
        sizeButton.classList.add('product-block__size--added');
        
        // Close modal if open (mobile)
        closeSizeModal();
        
        // Refresh the cart drawer contents using Symmetry theme events
        console.log('Dispatching cart-drawer:refresh');
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
          bubbles: true
        }));
        
        // Open the cart drawer using Symmetry theme events
        console.log('Dispatching cart-drawer:open');
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
        
        // Reset button after delay
        setTimeout(() => {
          sizeButton.textContent = originalText;
          sizeButton.disabled = false;
          sizeButton.classList.remove('product-block__size--added');
        }, 2000);
      })
      .catch(error => {
        console.error('Error adding to cart:', error);
        sizeButton.textContent = originalText;
        sizeButton.disabled = false;
        alert('Sorry, there was an error adding this item to your cart.');
      });
      return;
    }
  });
  
  // Initialize backdrop on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBackdrop);
  } else {
    initBackdrop();
  }
  
  console.log('Size selector initialized with event delegation');
})();

// ========================================
// PERSONALIZATION / PROPERTIES CODE
// ========================================

const changePreOrderText = (position) => {
  const preOrderField = document.querySelector('#properties-pre-order');
  if (preOrderField != undefined && preOrderField) {
    preOrderField.value = "";
    if (position !== '' && variantPreOrderText[position] !== '' && variantPreOrderText[position] !== undefined) {
      preOrderField.value = variantPreOrderText[position];
    }
  }
}

const backOrderContents = document.querySelectorAll('.js-backorder-copy');
const hideBackToOrderPDP = () => {
  if (backOrderContents.length) {
    backOrderContents.forEach((backOrderContent) => {
      backOrderContent.style.display = "none";
    });
  }
}
const showBackToOrderPDP = (position) => {
  const thisText = document.querySelector('.js-backorder-copy.backorder-copy--' + position);
  if (thisText !== null) {
    thisText.style.display = "block";
  }
}
const showHideAddToCart = (addToCartButton, notifyWhenAvailButton, selectedVariant) => {
  if (addToCartButton !== undefined && addToCartButton !== null && notifyWhenAvailButton !== undefined && notifyWhenAvailButton !== null) {
    console.log(selectedVariant, selectedVariant.classList.contains('is-unavailable'));
    if (selectedVariant.classList.contains('is-unavailable')) {
      addToCartButton.style.display = 'none'
    } else {
      addToCartButton.style.display = 'block'
    }
  }
}
const variantButtons = document.querySelectorAll('.js-variant-button');
const addToCartButton = document.querySelector('.js-add-to-cart-button');
const notifyWhenAvailButton = document.querySelector('.klaviyo-bis-trigger');

if (backOrderContents.length) {
  hideBackToOrderPDP();
}
if (variantButtons.length) {
  variantButtons.forEach((variantButton) => {
    if (variantButton.getAttribute('checked') !== null) {
      showBackToOrderPDP(variantButton.dataset.variantPosition);
      changePreOrderText(variantButton.dataset.variantPosition);
      setTimeout(() => {
        showHideAddToCart(addToCartButton, notifyWhenAvailButton, variantButton);
      }, 1000);
    }
    variantButton.addEventListener('change', (event) => {
      changePreOrderText(event.target.dataset.variantPosition);
      showHideAddToCart(addToCartButton, notifyWhenAvailButton, event.target);
      if (addToCartButton.dataset.preOrder == 1) {
        setTimeout(() => {
          hideBackToOrderPDP();
          showBackToOrderPDP(event.target.dataset.variantPosition);
          if (variantStock[event.target.dataset.variantPosition] <= 0) {
            addToCartButton.innerHTML = theme.strings.products_product_pre_order;
          } else {
            addToCartButton.innerHTML = theme.strings.products_product_add_to_cart;
          }
        }, 100);
      }
    });
  })
}

/**
 * Initialises a button that updates the product form's properties with the relevant availability text.
 * This is currently used for product page 'AddToCart' or 'Add Personalisation' buttons for HOM-3.
 * @param {HTMLElement} button 
 * @param {HTMLElement|null} [form] - Optional, will just use document.querySelector() if no param passed in
 */
const initPropertiesChangeToButton = (button, form) => {

  if (!button) {
    console.warn('No button passed in to initPropertiesChangeToButton')
    return
  }

  const queryElem = form ? form : document

  button.addEventListener("click", () => {
    // Availability section
    const deliveryAvailabilityText = queryElem.querySelector('.js-availability');
    const pickUpAvailabilityText = queryElem.querySelector('.store-availability-information__title');
    const deliveryProp = queryElem.querySelector('.js-prop-delivery-availability');
    const pickupProp = queryElem.querySelector('.js-prop-pickup-availability');
    
    if (deliveryAvailabilityText !== null && deliveryAvailabilityText !== undefined && pickUpAvailabilityText !== null && pickUpAvailabilityText !== undefined) {
      deliveryProp.value = deliveryAvailabilityText.innerText.trim();
      pickupProp.value = pickUpAvailabilityText.innerText.trim();
    }
  });
}

initPropertiesChangeToButton(addToCartButton);
  
// Insurance Add/Remove from Cart
const closeShippingInsurancePopovers = (exceptButton) => {
  document.querySelectorAll('.shipping-insurance-info[aria-expanded="true"]').forEach((button) => {
    if (button === exceptButton) return;

    const popover = document.getElementById(button.getAttribute('aria-controls'));
    button.setAttribute('aria-expanded', 'false');

    if (popover) {
      popover.classList.remove('is-open');
      popover.setAttribute('aria-hidden', 'true');
    }
  });
};

document.addEventListener('click', (event) => {
  const eventTarget = event.target instanceof Element ? event.target : event.target.parentElement;
  if (!eventTarget) return;

  const infoButton = eventTarget.closest('.shipping-insurance-info');

  if (infoButton) {
    const popover = document.getElementById(infoButton.getAttribute('aria-controls'));
    const isExpanded = infoButton.getAttribute('aria-expanded') === 'true';

    closeShippingInsurancePopovers(infoButton);
    infoButton.setAttribute('aria-expanded', String(!isExpanded));

    if (popover) {
      popover.classList.toggle('is-open', !isExpanded);
      popover.setAttribute('aria-hidden', String(isExpanded));
    }

    return;
  }

  if (!eventTarget.closest('.shipping-insurance-popover')) {
    closeShippingInsurancePopovers();
  }
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') {
    closeShippingInsurancePopovers();
  }
});

document.addEventListener('change', (event) => {
  const eventTarget = event.target instanceof Element ? event.target : event.target.parentElement;
  if (!eventTarget) return;

  const insuranceCheckbox = eventTarget.closest('input.shipping-switch-input');
  if (!insuranceCheckbox) return;

  event.preventDefault();

  const insuranceId = insuranceCheckbox.value;
  if (!insuranceId) {
    console.error('Error: Insurance product ID is missing.');
    return;
  }

  const isChecked = insuranceCheckbox.checked;
  const url = isChecked ? '/cart/add.js' : '/cart/change.js';
  const bodyData = isChecked
    ? { id: insuranceId, quantity: 1 }
    : { id: insuranceId, quantity: 0 };

  insuranceCheckbox.disabled = true;

  fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(bodyData)
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Shipping insurance request failed: ${response.status}`);
      }

      return response.json();
    })
    .then(() => {
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
        bubbles: true
      }));
    })
    .catch((error) => {
      insuranceCheckbox.checked = !isChecked;
      console.error('Error:', error);
    })
    .finally(() => {
      insuranceCheckbox.disabled = false;
    });
});

/**
 * =================================================================================================
 * Yotpo product-reviews redesign (Homebodii / LPG CRO)
 * =================================================================================================
 *
 * Yotpo renders the main reviews widget as app HTML. We can't touch its Liquid, so this module
 * re-shapes the rendered markup to match the Figma "Customer Reviews" design:
 *  - Reveals reviews in batches of three behind a branded "LOAD 3 MORE" button that delegates
 *    fetching to Yotpo's native pagination.
 *  - Injects a Figma-style "1-N of X Reviews" range count and a "Viewing" line under the button.
 *  - Reorders the reviewer name to sit directly under the star rating/title.
 *  - Pulls each review's sibling "was this helpful?" vote row into its matching review card.
 *  - Swaps the app-rendered mobile filter icon for the requested sliders icon.
 *
 * Yotpo renders asynchronously (and re-renders on pagination), so a MutationObserver re-runs the
 * transform whenever the widget markup changes.
 */
(function () {
  const ROOT_SELECTOR = '.yotpo-widget-instance #yotpo-reviews-main-widget';
  const REVIEWS_PER_PAGE = 3;

  // Sliders icon requested for the mobile "All Filters" control.
  const FILTER_ICON_SVG = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M1 6H19" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path d="M1 14H19" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path d="M7 9C8.65685 9 10 7.65685 10 6C10 4.34315 8.65685 3 7 3C5.34315 3 4 4.34315 4 6C4 7.65685 5.34315 9 7 9Z" fill="var(--hb-reviews-icon-bg, #ffffff)" stroke="currentColor" stroke-width="1.5"/><path d="M13 17C14.6569 17 16 15.6569 16 14C16 12.3431 14.6569 11 13 11C11.3431 11 10 12.3431 10 14C10 15.6569 11.3431 17 13 17Z" fill="var(--hb-reviews-icon-bg, #ffffff)" stroke="currentColor" stroke-width="1.5"/></svg>';

  // The helpful/report vote row is rendered as a sibling of each review inside the reviews list.
  // Walk forward from the review until we hit the vote row (or the next review) to pair them up.
  const getAssociatedVotes = (review) => {
    let sibling = review.nextElementSibling;
    while (sibling) {
      if (sibling.classList.contains('yotpo-review-votes-wrapper')) {
        return sibling;
      }
      if (sibling.classList.contains('yotpo-review')) {
        break;
      }
      sibling = sibling.nextElementSibling;
    }
    return null;
  };

  // Replace Yotpo's app-rendered mobile filter icon with the Figma sliders icon once it exists.
  const syncFilterIcon = (root) => {
    const button = root.querySelector('.yotpo-mobile-filters-btn');
    if (!button || button.dataset.hbFilterIcon === 'true') {
      return;
    }
    const currentIcon = button.querySelector('svg');
    if (!currentIcon) {
      return;
    }
    currentIcon.outerHTML = FILTER_ICON_SVG;
    // Relabel Yotpo's "Filters" control to "All Filters" to match the Figma filter section.
    const label = Array.from(button.querySelectorAll('*')).find(
      (el) => el.children.length === 0 && /^\s*filters\s*$/i.test(el.textContent)
    );
    if (label) {
      label.textContent = 'All Filters';
    }
    button.dataset.hbFilterIcon = 'true';
  };

  // Pull the real total from Yotpo's bottom-line copy ("Based on 114 reviews") so the range count
  // reflects the full review set rather than only the batch Yotpo has currently rendered.
  const getReviewTotal = (root) => {
    const source = root.querySelector('.yotpo-bottom-line-basic-text, .yotpo-sr-bottom-line-text');
    if (!source) {
      return 0;
    }
    const match = source.textContent.replace(/,/g, '').match(/(\d+)\s+reviews?/i);
    return match ? parseInt(match[1], 10) : 0;
  };

  // Reorder a single review card to the Figma layout: name under the rating, votes inside the card.
  const restyleReviewCard = (review) => {
    const centerPanel = review.querySelector('.yotpo-review-center-panel');
    const ratingTitle = review.querySelector('.yotpo-review-rating-title');

    const originalName = review.querySelector('.yotpo-reviewer-name');
    if (ratingTitle && originalName && !review.querySelector('.homebodii-yotpo-reviewer-name')) {
      const nameElement = originalName.cloneNode(true);
      nameElement.classList.add('homebodii-yotpo-reviewer-name');
      ratingTitle.insertAdjacentElement('afterend', nameElement);
    }

    // Pull the star rating into the card flow (Figma order: title → stars → name → body). It ships
    // in the left panel next to the avatar, which the redesign hides.
    const stars = review.querySelector('.yotpo-review-star-rating');
    if (centerPanel && stars && stars.parentElement !== centerPanel) {
      centerPanel.appendChild(stars);
    }

    const votes = getAssociatedVotes(review);
    if (centerPanel && votes && votes.parentElement !== centerPanel) {
      centerPanel.append(votes);
    }
  };

  const ensureRangeCount = (container, list) => {
    let count = container.querySelector('.homebodii-yotpo-review-count');
    if (!count) {
      count = document.createElement('div');
      count.className = 'homebodii-yotpo-review-count';
      container.insertBefore(count, list);
    }
    return count;
  };

  const ensureLoadMore = (root, container) => {
    let wrap = container.querySelector('.homebodii-yotpo-load-more-wrap');
    if (wrap) {
      return wrap;
    }

    wrap = document.createElement('div');
    wrap.className = 'homebodii-yotpo-load-more-wrap';

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'homebodii-yotpo-load-more';
    button.textContent = 'LOAD 3 MORE';
    button.addEventListener('click', () => {
      const rendered = root.querySelectorAll('.yotpo-review').length;
      const shown = parseInt(root.dataset.hbShown || String(REVIEWS_PER_PAGE), 10);
      root.dataset.hbShown = String(shown + REVIEWS_PER_PAGE);

      if (shown >= rendered) {
        // We've revealed everything Yotpo has fetched — ask Yotpo for the next page.
        const nativeLoadMore = root.querySelector(
          '.yotpo-reviews-pagination-container a[aria-label*="next" i]:not(.disabled)'
        );
        if (nativeLoadMore) {
          nativeLoadMore.click();
          return;
        }
      }
      applyRedesign(root);
    });

    const viewing = document.createElement('p');
    viewing.className = 'homebodii-yotpo-viewing';

    wrap.append(button, viewing);
    container.append(wrap);
    return wrap;
  };

  const applyRedesign = (root) => {
    const list = root.querySelector('.yotpo-reviews-list');
    const container = list ? list.parentElement : null;
    const reviews = list ? Array.from(list.querySelectorAll(':scope > .yotpo-review')) : [];
    if (!list || !container || !reviews.length) {
      return;
    }

    const total = getReviewTotal(root);

    let shown = parseInt(root.dataset.hbShown || String(REVIEWS_PER_PAGE), 10);
    if (shown < REVIEWS_PER_PAGE) {
      shown = REVIEWS_PER_PAGE;
    }
    if (shown > reviews.length) {
      shown = reviews.length;
    }
    root.dataset.hbShown = String(shown);

    reviews.forEach((review, index) => {
      const visible = index < shown;
      review.hidden = !visible;
      if (visible) {
        restyleReviewCard(review);
      }
    });

    // Any vote row still sitting as a direct child of the list belongs to a hidden (or unmatched)
    // review — hide it so orphan "helpful?" controls never dangle beneath the visible cards.
    list.querySelectorAll(':scope > .yotpo-review-votes-wrapper').forEach((votes) => {
      votes.hidden = true;
    });

    // Only keep a divider that sits before a visible review. Yotpo appends trailing
    // .yotpo-review-border-smooth spacers that would otherwise stack into a multi-line border.
    list.querySelectorAll(':scope > .yotpo-review-border-smooth').forEach((divider) => {
      let sibling = divider.nextElementSibling;
      let hasVisibleReviewAfter = false;
      while (sibling) {
        if (sibling.classList.contains('yotpo-review') && !sibling.hidden) {
          hasVisibleReviewAfter = true;
          break;
        }
        sibling = sibling.nextElementSibling;
      }
      divider.hidden = !hasVisibleReviewAfter;
    });

    const visibleCount = Math.min(shown, reviews.length);
    const total_or_length = total || reviews.length;

    const count = ensureRangeCount(container, list);
    count.textContent = `1-${visibleCount} of ${total || reviews.length} Reviews`;

    const wrap = ensureLoadMore(root, container);
    const button = wrap.querySelector('.homebodii-yotpo-load-more');
    const viewing = wrap.querySelector('.homebodii-yotpo-viewing');
    viewing.textContent = `Viewing 1-${visibleCount} of ${total_or_length}`;

    const moreAvailable = visibleCount < reviews.length || (total && visibleCount < total);
    wrap.hidden = !moreAvailable;
    if (button) {
      button.hidden = !moreAvailable;
    }
  };

  // Yotpo only renders a single value label ("Runs Large") under each review's Fit bar. The Figma
  // wants the full Runs Small / True To Size / Runs Large scale beneath every fit slider, so inject
  // it once per fit question. The marker itself is already positioned to the value by Yotpo.
  const FIT_SCALE_LABELS = ['Runs Small', 'True To Size', 'Runs Large'];
  const addFitScales = (root) => {
    root.querySelectorAll('.yotpo-custom-questions-range-question-wrapper').forEach((wrap) => {
      if (wrap.dataset.hbFitScale === 'true') {
        return;
      }
      const title = wrap.querySelector('.yotpo-custom-questions-range-question-title');
      if (!title || title.textContent.trim().toLowerCase() !== 'fit') {
        return;
      }
      const barWrapper = wrap.querySelector('.yotpo-progress-bar-wrapper');
      if (!barWrapper) {
        return;
      }
      const scale = document.createElement('div');
      scale.className = 'homebodii-yotpo-fit-scale';
      FIT_SCALE_LABELS.forEach((label) => {
        const span = document.createElement('span');
        span.textContent = label;
        scale.appendChild(span);
      });
      barWrapper.insertAdjacentElement('afterend', scale);
      wrap.dataset.hbFitScale = 'true';
    });
  };

  // Replace Yotpo's default "helpful?" thumbs with the Figma outline icons. We swap the path/viewBox
  // in place (keeping the <svg> element) so Yotpo's own vote click handlers keep working.
  const VOTE_UP_PATH = '<path d="M5.83292 8.33322V18.334M12.5 4.89961L11.6666 8.33322H16.5252C16.784 8.33322 17.0392 8.39346 17.2706 8.50918C17.5021 8.62491 17.7034 8.79293 17.8586 8.99994C18.0139 9.20695 18.1188 9.44726 18.1651 9.70185C18.2114 9.95644 18.1978 10.2183 18.1253 10.4667L16.1835 17.1339C16.0826 17.4801 15.872 17.7843 15.5835 18.0007C15.295 18.217 14.9441 18.334 14.5834 18.334H3.33277C2.89071 18.334 2.46676 18.1584 2.15419 17.8458C1.84161 17.5332 1.666 17.1093 1.666 16.6672V10C1.666 9.55795 1.84161 9.134 2.15419 8.82141C2.46676 8.50882 2.89071 8.33322 3.33277 8.33322H5.63291C5.943 8.33305 6.24689 8.24638 6.51042 8.08296C6.77396 7.91954 6.98667 7.68584 7.12466 7.40814L9.99984 1.66602C10.3928 1.67088 10.7797 1.7645 11.1314 1.93987C11.4832 2.11524 11.7907 2.36783 12.0311 2.67877C12.2715 2.9897 12.4386 3.35095 12.5198 3.73552C12.601 4.12008 12.5942 4.51802 12.5 4.89961Z" stroke="#57585B" stroke-linecap="round"/>';
  const VOTE_DOWN_PATH = '<path d="M12.8591 10.5008V0.49997M6.19201 13.9344L7.0254 10.5008H2.16677C1.90801 10.5008 1.65281 10.4405 1.42137 10.3248C1.18993 10.2091 0.988609 10.0411 0.833355 9.83405C0.6781 9.62704 0.573175 9.38672 0.526887 9.13214C0.480599 8.87755 0.49422 8.61568 0.566672 8.36726L2.50846 1.70007C2.60944 1.35385 2.81998 1.04972 3.10849 0.833329C3.397 0.616942 3.74791 0.49997 4.10855 0.49997H15.3592C15.8013 0.49997 16.2252 0.675579 16.5378 0.988164C16.8504 1.30075 17.026 1.72471 17.026 2.16677V8.83397C17.026 9.27603 16.8504 9.69999 16.5378 10.0126C16.2252 10.3252 15.8013 10.5008 15.3592 10.5008H13.0591C12.749 10.5009 12.4451 10.5876 12.1816 10.751C11.918 10.9144 11.7053 11.1481 11.5673 11.4258L8.69216 17.168C8.29916 17.1631 7.91233 17.0695 7.56059 16.8941C7.20885 16.7187 6.90128 16.4662 6.66087 16.1552C6.42046 15.8443 6.25342 15.483 6.17223 15.0985C6.09104 14.7139 6.0978 14.316 6.19201 13.9344Z" stroke="#57585B" stroke-linecap="round"/>';
  const VOTE_UP_SVG = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">' + VOTE_UP_PATH + '</svg>';
  const VOTE_DOWN_SVG = '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">' + VOTE_DOWN_PATH + '</svg>';
  const syncVoteIcons = (root) => {
    // Replace the whole <svg> (parsed via a temp container so the paths land in the SVG namespace
    // and actually render), preserving Yotpo's class so its vote click handlers keep firing.
    const swap = (selector, markup) => {
      root.querySelectorAll(selector).forEach((svg) => {
        if (svg.dataset.hbVoteIcon === 'true') {
          return;
        }
        const cls = svg.getAttribute('class') || '';
        const temp = document.createElement('div');
        temp.innerHTML = markup;
        const newSvg = temp.firstElementChild;
        if (!newSvg) {
          return;
        }
        if (cls) {
          newSvg.setAttribute('class', cls);
        }
        newSvg.dataset.hbVoteIcon = 'true';
        svg.replaceWith(newSvg);
      });
    };
    swap('svg.yotpo-review-votes-icon-up', VOTE_UP_SVG);
    swap('svg.yotpo-review-votes-icon-down', VOTE_DOWN_SVG);
    // Shorten "Was this review helpful?" to "Helpful?" to match the Figma card.
    root.querySelectorAll('.yotpo-review-votes-text').forEach((el) => {
      if (/helpful/i.test(el.textContent) && el.textContent.trim() !== 'Helpful?') {
        el.textContent = 'Helpful?';
      }
    });
  };

  // Reformat the summary copy to the Figma: "<score> Stars" (left) and "<n> Reviews" (right).
  const restyleSummary = (root) => {
    const score = root.querySelector('.yotpo-bottom-line-score');
    if (score && !/stars/i.test(score.textContent)) {
      score.textContent = score.textContent.trim() + ' Stars';
    }
    const count = root.querySelector('.yotpo-bottom-line-basic-text');
    if (count) {
      const match = count.textContent.match(/([\d,]+)\s*reviews?/i);
      if (match && !/^[\d,]+\s*Reviews$/.test(count.textContent.trim())) {
        count.textContent = match[1] + ' Reviews';
      }
    }
  };

  const scanForYotpoReviews = () => {
    const root = document.querySelector(ROOT_SELECTOR);
    if (!root || !root.querySelector('.yotpo-review')) {
      return;
    }
    root.classList.add('homebodii-yotpo-redesign');
    syncFilterIcon(root);
    addFitScales(root);
    syncVoteIcons(root);
    restyleSummary(root);
    applyRedesign(root);
  };

  // Yotpo renders (and re-renders on pagination/filtering) asynchronously. A body-wide observer can
  // fire hundreds of times during a single render burst, and running the full scan on every one of
  // those callbacks caused long tasks / page hangs. So we:
  //   1. Coalesce every mutation in a frame into a SINGLE scan via requestAnimationFrame (debounce).
  //   2. Narrow the observer to the Yotpo widget container once it exists, so unrelated page
  //      mutations (other apps, tracking, lazy images) no longer trigger scans at all.
  const getObserveTarget = () =>
    document.querySelector('.yotpo-widget-instance') ||
    document.querySelector('#yotpo-reviews-main-widget') ||
    document.body;

  let scanScheduled = false;

  const observe = () => {
    observer.observe(getObserveTarget(), { childList: true, subtree: true });
  };

  const observer = new MutationObserver(() => {
    if (scanScheduled) {
      return;
    }
    scanScheduled = true;
    window.requestAnimationFrame(() => {
      scanScheduled = false;
      // Disconnect around our own DOM writes so they never re-trigger the observer.
      observer.disconnect();
      try {
        scanForYotpoReviews();
      } finally {
        observe();
      }
    });
  });

  const start = () => {
    observe();
    scanForYotpoReviews();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

/**
 * =================================================================================================
 * Klaviyo "For You" hub launcher — show at top, hide on scroll
 * =================================================================================================
 *
 * Klaviyo's floating hub launcher ships an appear-on-scroll behaviour (hidden at the top of the
 * page, revealed once you scroll). We want the reverse: visible above the fold, then gone once the
 * shopper scrolls past roughly one screen.
 *
 * The show/hide styling lives in custom.css; this module just toggles `body.kl-hub-scrolled` based
 * on scroll position. Reads are coalesced into a single requestAnimationFrame per scroll burst so we
 * never touch layout on every scroll event.
 */
(function () {
  const SCROLLED_CLASS = 'kl-hub-scrolled';
  // Hide once the shopper has scrolled past ~one screen (the hub has served its "above the fold"
  // purpose by then). 0.9 keeps it visible for the whole first viewport before fading.
  const threshold = () => window.innerHeight * 0.9;

  let ticking = false;

  const apply = () => {
    ticking = false;
    document.body.classList.toggle(SCROLLED_CLASS, window.scrollY > threshold());
  };

  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(apply);
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  apply();
})();
